import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { ThemeProvider } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Alert, Autocomplete } from '@mui/material';
import { Snackbar, TextField } from '@mui/material';
import ButtonGroup from '@mui/material/ButtonGroup';
import * as ReactDOM from 'react-dom';
import { selectMainTheme } from '../../store/fuse/settingsSlice';
import ConfirmationDialog from '../../ui-components/ConfirmationDialog';
import firebaseService from '@ameroservices-platform/shared/services/firebase';
import resyncDestinations from '@ameroservices-platform/shared/enums/resyncDestinations';
import resyncTypes from '@ameroservices-platform/shared/enums/resyncTypes';
import { resyncItemsToSyncEngine } from './SyncFirestore';

const defaultResyncTypes = {
	[resyncTypes.CUSTOMERS]: 'Kunder',
};

const defaultResyncDestinations = {
	[resyncDestinations.ELASTIC_SEARCH]: 'Elastic search',
};

function SyncAdminModal({ open, onClose }) {
	const mainTheme = useSelector(selectMainTheme);
	const [loading, setLoading] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [alert, setAlert] = useState(null);
	const [chosenResyncOption, setChosenResyncOption] = useState(null);
	const [chosenResyncDestination, setChosenResyncDestination] = useState(null);
	const [currentResyncTypes, setCurrentResyncTypes] = useState(defaultResyncTypes);
	const [currentResyncDestinations, setCurrentResyncDestinations] = useState(defaultResyncDestinations);
	const [snackbarMessage, setSnackbarMessage] = useState();

	useEffect(() => {
		const newResyncTypes = {
			[resyncTypes.ORDERS]: 'Ordre',
			[resyncTypes.BIG_QUERY]: 'Big query',
		};
		if (process.env.NODE_ENV === 'development') {
			setCurrentResyncTypes((oldTypes) => {
				const copyTypes = { ...oldTypes };
				return {
					...copyTypes,
					...newResyncTypes,
					[resyncTypes.LOCAL_ELASTIC_SEARCH]: 'Elastic Search Lokalt',
				};
			});
		} else {
			setCurrentResyncTypes((oldTypes) => {
				const copyTypes = { ...oldTypes };
				return {
					...copyTypes,
					...newResyncTypes,
				};
			});
		}
		setCurrentResyncDestinations((_currentResyncDestinations) => {
			return {
				..._currentResyncDestinations,
			};
		});
	}, []);

	useEffect(() => {
		resetFormData();
	}, []);

	const getResyncOptionLabel = (resyncOption) => currentResyncTypes[resyncOption];

	const getDestinationOptionLabel = (destinationOption) => currentResyncDestinations[destinationOption];

	const resetFormData = () => {
		setChosenResyncOption(null);
		setChosenResyncDestination(null);
		setAlert(null);
	};

	const handleOnCloseDialog = async () => {
		onClose();
		resetFormData();
	};

	const handleOnClickResyncButton = async () => {
		setShowConfirmationDialog(true);
	};

	const handleResync = async (success) => {
		setShowConfirmationDialog(false);
		if (success) {
			setLoading(true);
			if (chosenResyncOption === 'localElasticSearch') {
				await firebaseService.callFunctionByName('sysAdminMigrationRunJob', {
					job: 'resyncToElasticSearch',
				});
			} else if (chosenResyncOption === 'bigQuery') {
				await firebaseService.callFunctionByName('bigQueryResyncBigQuery', {});
			} else {
				await resyncItemsToSyncEngine(chosenResyncOption, chosenResyncDestination);
			}

			setLoading(false);
			setSnackbarMessage(`Resynkronisering af ${getResyncOptionLabel(chosenResyncOption).toLowerCase()} startet`);
		}

		setShowConfirmationDialog(false);
		onClose();
	};

	const getDestinationOptions = () => {
		let destinationOptions = [];
		if (chosenResyncOption) {
			switch (chosenResyncOption) {
				case resyncTypes.CUSTOMERS:
				case resyncTypes.ORDERS:
					destinationOptions = [resyncDestinations.ELASTIC_SEARCH];
					break;
				default:
					destinationOptions = [];
			}
		}
		return destinationOptions;
	};

	const getResyncButtonDisabled = () => {
		let resyncButtonDisabled = true;
		if (chosenResyncOption) {
			if (chosenResyncOption === resyncTypes.CUSTOMERS || chosenResyncOption === resyncTypes.ORDERS) {
				if (chosenResyncDestination) {
					resyncButtonDisabled = false;
				}
			} else {
				resyncButtonDisabled = false;
			}
		}
		return resyncButtonDisabled;
	};

	return (
		<ThemeProvider theme={mainTheme}>
			<>
				<Dialog
					open={open}
					onClose={handleOnCloseDialog}
					aria-labelledby="form-dialog-title"
					classes={{
						paper: 'rounded-8 min-w-md',
					}}
				>
					<AppBar position="static">
						<Toolbar className="flex w-full">
							<Typography variant="subtitle1" color="inherit">
								Synkroniserings administration
							</Typography>
						</Toolbar>
					</AppBar>

					<DialogContent classes={{ root: 'p-16 pb-0 sm:p-24 sm:pb-0' }}>
						<Autocomplete
							className="mt-8 mb-16 w-full"
							options={Object.keys(currentResyncTypes)}
							value={chosenResyncOption}
							onChange={(event, newValue) => {
								setChosenResyncOption(newValue);
								setChosenResyncDestination(null);
								setAlert(null);
							}}
							getOptionLabel={getResyncOptionLabel}
							renderInput={(params) => (
								<TextField {...params} label="Resynkroniser..." variant="outlined" />
							)}
							openOnFocus
							disabled={loading}
						/>
						{chosenResyncOption &&
							(chosenResyncOption === resyncTypes.CUSTOMERS ||
								chosenResyncOption === resyncTypes.ORDERS) && (
								<Autocomplete
									className="mt-8 mb-16 w-full"
									options={getDestinationOptions()}
									value={chosenResyncDestination}
									onChange={(event, newValue) => {
										setChosenResyncDestination(newValue);
									}}
									getOptionLabel={getDestinationOptionLabel}
									renderInput={(params) => (
										<TextField {...params} label="Vælg destination" variant="outlined" />
									)}
									openOnFocus
									disabled={loading}
								/>
							)}
						{alert && (
							<Alert severity={alert.severity} className="mb-10">
								{alert.message}
							</Alert>
						)}
						<ConfirmationDialog
							onClose={handleResync}
							keepMounted
							open={showConfirmationDialog}
							needToType={getResyncOptionLabel(chosenResyncOption)}
							content={`Er du sikker på, at du vil resynkronisere ${getResyncOptionLabel(
								chosenResyncOption
							)}?`}
							okButton="Ja"
							cancelButton="Nej"
							loading={loading}
							maxWidth="sm"
						/>
					</DialogContent>
					<DialogActions className="justify-end p-8">
						<div className="px-16">
							<ButtonGroup>
								<Button variant="outlined" onClick={handleOnCloseDialog}>
									Annuller
								</Button>
								<Button
									variant="contained"
									color="primary"
									onClick={handleOnClickResyncButton}
									disabled={getResyncButtonDisabled()}
								>
									Resynkroniser
								</Button>
							</ButtonGroup>
						</div>
					</DialogActions>
				</Dialog>
				{ReactDOM.createPortal(
					<Snackbar
						autoHideDuration={10000}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						open={!!snackbarMessage}
						onClose={() => setSnackbarMessage(null)}
						message={<span>{snackbarMessage}</span>}
					/>,
					document.body
				)}
			</>
		</ThemeProvider>
	);
}

export default SyncAdminModal;
